import React, { useState, useEffect } from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import twilioClient from '../../utils/twilioClient';

const { FiPlus, FiEdit2, FiTrash2, FiPhone, FiLayers, FiSave, FiX, FiLink, FiInfo, FiMessageSquare } = FiIcons;

const IVRConsole = () => {
  const [menus, setMenus] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingMenu, setEditingMenu] = useState(null);
  const [showOptionModal, setShowOptionModal] = useState(false);
  const [currentOption, setCurrentOption] = useState({ key: '', action: 'say', value: '' });
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);
  const [attachingMenu, setAttachingMenu] = useState(null);
  const [selectedNumber, setSelectedNumber] = useState('');
  const [smsForwardTo, setSmsForwardTo] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      // Use new backend proxy endpoint
      const menusRes = await fetch('/api/ncb/get-ivr-menus');
      
      if (!menusRes.ok) {
        console.warn("[IVR] Failed to load IVR menus (status " + menusRes.status + ")");
        setMenus([]);
      } else {
        const data = await menusRes.json();
        
        // Handle generic list or {menus: [...]} or {data: [...]}
        const rows = Array.isArray(data) ? data : (data.menus || data.data || []);
        
        const normalized = rows.map((row) => {
          let options = [];
          try {
            // Options might be stored as JSON string or already be an object
            options = typeof row.options === 'string' ? JSON.parse(row.options) : (row.options || []);
            if (!Array.isArray(options)) options = [];
          } catch (_) {
            options = [];
          }
          
          return {
            id: row.menu_id || row.id, // Prefer menu_id
            prompt: row.title || row.prompt || "Untitled Menu",
            description: row.description || "",
            options
          };
        });
        
        setMenus(normalized);
      }
    } catch (error) {
      console.error('[IVR] Unexpected error loading IVR menus:', error);
      setMenus([]);
    }

    // Load Twilio numbers
    try {
      const numbersData = await twilioClient.getNumbers();
      setNumbers(numbersData.numbers || []);
    } catch (error) {
      console.error('Failed to load numbers:', error);
      setNumbers([]);
    }

    try {
      const forwardTo = import.meta.env.VITE_TWILIO_SMS_FORWARD_TO || '';
      setSmsForwardTo(forwardTo);
    } catch (e) {
      console.warn('Error reading SMS forwarding env var', e);
    }
    
    setLoading(false);
  };

  const saveMenu = async (menu) => {
    try {
      const payload = {
        title: menu.prompt,
        description: menu.description || "",
        options: menu.options || []
      };

      if (menu.id) {
        // Update existing menu
        payload.id = menu.id;
        payload.menu_id = menu.id; // Ensure menu_id is passed for consistency
        
        const res = await fetch('/api/ncb/update-ivr-menu', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Update failed');
        }
      } else {
        // Create new menu
        // Generate a new ID if creating
        payload.menu_id = `ivr_${Date.now()}`;
        
        const res = await fetch('/api/ncb/create-ivr-menu', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || 'Create failed');
        }
      }
      
      loadData();
      setEditingMenu(null);
    } catch (error) {
      alert('Failed to save menu: ' + error.message);
    }
  };

  const deleteMenu = async (menuId) => {
    if (!confirm('Are you sure you want to delete this menu?')) return;
    try {
      const res = await fetch('/api/ncb/delete-ivr-menu', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: menuId })
      });
      
      if (!res.ok) throw new Error('Delete failed');
      
      loadData();
    } catch (error) {
      alert('Failed to delete menu: ' + error.message);
    }
  };

  const attachMenuToNumber = async () => {
    if (!attachingMenu || !selectedNumber) return;
    try {
      await twilioClient.updateNumber(selectedNumber, {
        voiceWebhookType: 'ivr',
        ivrMenu: String(attachingMenu)
      });
      alert('IVR menu attached successfully!');
      setAttachingMenu(null);
      setSelectedNumber('');
      loadData();
    } catch (error) {
      alert('Failed to attach menu: ' + error.message);
    }
  };

  const addOption = () => {
    if (!editingMenu) return;
    const updatedMenu = { ...editingMenu };
    if (!updatedMenu.options) updatedMenu.options = [];
    
    if (editingOptionIndex !== null) {
      updatedMenu.options[editingOptionIndex] = { ...currentOption };
    } else {
      updatedMenu.options.push({ ...currentOption });
    }
    
    setEditingMenu(updatedMenu);
    setShowOptionModal(false);
    setCurrentOption({ key: '', action: 'say', value: '' });
    setEditingOptionIndex(null);
  };

  const removeOption = (index) => {
    if (!editingMenu) return;
    const updatedMenu = { ...editingMenu };
    updatedMenu.options.splice(index, 1);
    setEditingMenu(updatedMenu);
  };

  const editOption = (index, option) => {
    setCurrentOption({ ...option });
    setEditingOptionIndex(index);
    setShowOptionModal(true);
  };

  if (editingMenu) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {editingMenu.id ? `Edit Menu` : 'Create New IVR Menu'}
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setEditingMenu(null)}
              className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
            >
              <FiX className="text-xl" />
            </button>
            <button
              onClick={() => saveMenu(editingMenu)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              <FiSave className="mr-2" /> Save Menu
            </button>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border space-y-4">
          {editingMenu.id && (
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-500">Menu ID</label>
              <input
                type="text"
                className="w-full border rounded p-2 bg-gray-50 text-gray-500"
                value={editingMenu.id}
                readOnly
              />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium mb-1">Title / Prompt Text</label>
            <textarea
              className="w-full border rounded p-2"
              rows={3}
              value={editingMenu.prompt || ''}
              onChange={(e) => setEditingMenu({ ...editingMenu, prompt: e.target.value })}
              placeholder="Welcome. Press 1 for sales, 2 for support..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description (Optional)</label>
            <input
              type="text"
              className="w-full border rounded p-2"
              value={editingMenu.description || ''}
              onChange={(e) => setEditingMenu({ ...editingMenu, description: e.target.value })}
              placeholder="Main company greeting"
            />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Menu Options</h3>
            <button
              onClick={() => setShowOptionModal(true)}
              className="flex items-center px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              <FiPlus className="mr-2" /> Add Option
            </button>
          </div>

          {editingMenu.options && editingMenu.options.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Key</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Value / Target</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {editingMenu.options.map((option, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{option.key}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm capitalize">{option.action}</td>
                      <td className="px-6 py-4 text-sm truncate max-w-xs">{option.value}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <button
                          onClick={() => editOption(index, option)}
                          className="text-blue-600 hover:text-blue-800 mr-2"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          onClick={() => removeOption(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FiTrash2 />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-4">No options added yet.</p>
          )}
        </div>

        {/* Option Modal */}
        {showOptionModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-semibold mb-4">
                {editingOptionIndex !== null ? 'Edit Option' : 'Add Option'}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Key (0-9)</label>
                  <input
                    type="text"
                    className="w-full border rounded p-2"
                    value={currentOption.key}
                    onChange={(e) => setCurrentOption({ ...currentOption, key: e.target.value })}
                    maxLength={1}
                    placeholder="1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Action Type</label>
                  <select
                    className="w-full border rounded p-2"
                    value={currentOption.action}
                    onChange={(e) => setCurrentOption({ ...currentOption, action: e.target.value, value: '' })}
                  >
                    <option value="say">Say (Text to Speech)</option>
                    <option value="play">Play (Audio URL)</option>
                    <option value="forward">Forward (Phone Number)</option>
                    <option value="sms">Send SMS</option>
                    <option value="realtime">Real-time Stream</option>
                    <option value="goto">Go to Menu (Nested)</option>
                  </select>
                </div>
                
                {currentOption.action === 'goto' ? (
                  <div>
                    <label className="block text-sm font-medium mb-1">Target Menu</label>
                    <select
                      className="w-full border rounded p-2"
                      value={currentOption.value}
                      onChange={(e) => setCurrentOption({ ...currentOption, value: e.target.value })}
                    >
                      <option value="">Select a menu...</option>
                      {menus
                        .filter(m => m.id !== editingMenu.id) // Prevent self-reference loops
                        .map(m => (
                          <option key={m.id} value={m.id}>
                            {m.prompt ? m.prompt.substring(0, 30) : `Menu ${m.id}`}...
                          </option>
                        ))}
                    </select>
                  </div>
                ) : (
                  <div>
                    <label className="block text-sm font-medium mb-1">Value</label>
                    <input
                      type="text"
                      className="w-full border rounded p-2"
                      value={currentOption.value}
                      onChange={(e) => setCurrentOption({ ...currentOption, value: e.target.value })}
                      placeholder={
                        currentOption.action === 'say' ? 'Text to speak' :
                        currentOption.action === 'play' ? 'https://example.com/audio.mp3' :
                        currentOption.action === 'forward' ? '+15551234567' :
                        currentOption.action === 'sms' ? 'SMS message content' :
                        currentOption.action === 'realtime' ? 'wss://your-realtime-url' :
                        'Value'
                      }
                    />
                  </div>
                )}
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <button
                  onClick={() => setShowOptionModal(false)}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={addOption}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {editingOptionIndex !== null ? 'Update' : 'Add'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">IVR Menus</h2>
        <button
          onClick={() => setEditingMenu({})}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
        >
          <FiPlus className="mr-2" /> Create Menu
        </button>
      </div>

      {/* SMS Forwarding Status */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <SafeIcon icon={FiMessageSquare} className="mr-2 text-blue-500" />
            <span className="text-sm font-medium text-gray-700">SMS Forwarding:</span>
          </div>
          <div className="flex items-center">
            {smsForwardTo ? (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                Forwarding to {smsForwardTo}
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Forwarding OFF
              </span>
            )}
            <div className="ml-2 relative group">
              <SafeIcon icon={FiInfo} className="text-gray-400 cursor-help text-sm" />
              <div className="absolute right-0 top-full mt-1 w-64 p-2 bg-gray-800 text-white text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity z-10">
                Configure SMS forwarding in Settings to automatically forward incoming messages.
              </div>
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading IVR menus...</div>
      ) : menus.length === 0 ? (
        <div className="bg-white p-12 rounded-lg border border-dashed text-center">
          <FiLayers className="mx-auto text-4xl text-gray-300 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No IVR Menus Created</h3>
          <p className="text-gray-500 mb-4">Create your first IVR menu to handle incoming calls automatically.</p>
          <button
            onClick={() => setEditingMenu({})}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 mx-auto"
          >
            <FiPlus className="mr-2" /> Create Your First Menu
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {menus.map((menu) => (
            <div key={menu.id} className="bg-white p-6 rounded-lg shadow-sm border">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {menu.prompt ? (menu.prompt.length > 30 ? menu.prompt.substring(0, 30) + '...' : menu.prompt) : `Menu #${menu.id}`}
                  </h3>
                  {menu.description && (
                    <p className="text-gray-500 text-xs mt-1">{menu.description}</p>
                  )}
                </div>
                <div className="flex space-x-1">
                  <button
                    onClick={() => setEditingMenu(menu)}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    onClick={() => deleteMenu(menu.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </div>
              <div className="text-sm text-gray-500 mb-4">
                {menu.options?.length || 0} options
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setAttachingMenu(menu.id)}
                  className="flex items-center px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                >
                  <FiLink className="mr-1" /> Attach to Number
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Attach Menu Modal */}
      {attachingMenu && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold mb-4">Attach IVR Menu to Number</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Select Phone Number</label>
                <select
                  className="w-full border rounded p-2"
                  value={selectedNumber}
                  onChange={(e) => setSelectedNumber(e.target.value)}
                >
                  <option value="">Choose a number...</option>
                  {numbers.map((number) => (
                    <option key={number.sid} value={number.sid}>
                      {number.phoneNumber} ({number.friendlyName})
                    </option>
                  ))}
                </select>
              </div>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm text-gray-600">
                  <strong>Menu ID:</strong> {attachingMenu}
                </p>
                <p className="text-sm text-gray-600 break-all">
                  <strong>Webhook URL:</strong> {window.location.origin}/api/twilio/ivr/handle?CurrentMenu={attachingMenu}
                </p>
              </div>
            </div>
            <div className="flex justify-end space-x-2 mt-6">
              <button
                onClick={() => {
                  setAttachingMenu(null);
                  setSelectedNumber('');
                }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded"
              >
                Cancel
              </button>
              <button
                onClick={attachMenuToNumber}
                disabled={!selectedNumber}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
              >
                Attach Menu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IVRConsole;